import React, { useEffect, useState, useRef, useCallback } from 'react';
import styles from './ModalInputField.module.css';
import useTheme from 'config/useTheme';
import PrivateKeyField from 'components/shared/private-key-field/PrivateKeyField';

const ModalInputField = ({
    autoFocus = false,
    type = 'text',
    privateKey = false,
    placeholder,
    value,
    setValue,
    validator,
    valid,
}) => {
    const { dark } = useTheme();

    const [inputValue, setInputValue] = useState(value ?? '');
    const [isValid, setIsValid] = useState(true);
    const [wasTouched, setWasTouched] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    const debounceRef = useRef();

    // ✅ Sync local input if external value changes (but only if different)
    useEffect(() => {
        if (value !== inputValue) {
            setInputValue(value ?? '');
        }
        // `inputValue` je NAMERNO izostavljen: ovo poravnava lokalno polje sa
        // spoljnom vrednošću, pa bi zavisnost od lokalne vrednosti vraćala
        // effect na svaki otkucaj i otimala se korisniku.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    // ✅ Debounced validator and updater — only run if value changed
    useEffect(() => {
        clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            // ⚠️ Prevent unnecessary setValue loop
            if (setValue && inputValue !== value) {
                setValue(inputValue);
            }

            if (validator) {
                const valid = validator(inputValue);
                setIsValid(valid);
            }
        }, 400);

        return () => clearTimeout(debounceRef.current);
    }, [inputValue, value, setValue, validator]);

    const handleChange = useCallback((e) => {
        const newVal = e.target.value;
        setInputValue(newVal);

        if (!wasTouched) {
            setWasTouched(true);
        }
    }, [wasTouched]);

    const handleBlur = useCallback(() => {
        setShowValidation(true);
    }, []);

    const isPrivateKeyField = privateKey || type === 'password';
    const isFieldValid = typeof valid === "boolean" ? valid : isValid;

    return (
        <div
            className={`${styles.input_box} ${showValidation && !isFieldValid ? styles.invalid : ''
                } ${dark ? styles.dark : ''} ${inputValue ? styles.hasValue : ''}`}
        >
            {isPrivateKeyField ? (
                <PrivateKeyField
                    autoFocus={autoFocus}
                    value={inputValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={""}
                    inputClassName={styles.modalInput}
                    toggleClassName={styles.modalToggle}
                    wrapperStyle={{ height: "100%" }}
                />
            ) : (
                <input
                    autoFocus={autoFocus}
                    type={type}
                    placeholder=""
                    value={inputValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                />
            )}
            <span className={styles.placeholder}>{placeholder}</span>
        </div>
    );
};

export default React.memo(ModalInputField);
